import React, { useMemo, useState } from 'react';
import { cloneDeep, remove, get, filter, EMPTY_FN } from '@libs/shared';
import {
    Button,
    Message,
    Alert,
    Tooltip,
    VirtualTable,
    CheckCircleFilled,
    ExclamationCircleFilled,
    SvgIcon
} from '@fx-ui/fine-design';
import { getRequestUrl, hasFunctionContactPhone, post } from '../../../../utils';
import { authModel } from '../../../../models';
import { platformIntl, API } from '../../../../constants';
import { getScrollbarSize } from '../../utils';
import PencilEditIcon from '../../../../assets/svgicons/pencil-edit.svg';
import TrashIcon from '../../../../assets/svgicons/trash.svg';

interface IMessageReceiverTable {
    receivers: any[];
    editable?: boolean;
    onReceiverEdit?: (receiver: Record<string, any>) => void;
    onReceiverChange?: (receivers: any[]) => void;
    tableWidth: number;
}

interface IVerifiedData {
    phone?: string;
    email?: string;
    is_verified: boolean;
}

/**
 * 通知管理配置项
 *
 * @param props IMessageReceiverTable
 * @class
 */
const MessageReceiverTable: React.FC<IMessageReceiverTable> = (props) => {
    const {
        receivers,
        tableWidth,
        editable = true,
        onReceiverEdit = EMPTY_FN,
        onReceiverChange = EMPTY_FN
    } = props;
    const [alertShow, setAlertShow] = useState(false);
    const [currentRowData, setCurrentRowData] = useState<Record<string, any>>({});

    /*
     * 发送验证消息
     *
     * @param targetId 提醒对象id
     * @param channel 类型
     */
    const sendValidation = (targetId: string, channel: string) => {
        post(getRequestUrl(API.message.validate_receiver, authModel.tenantId), {
            _id: targetId,
            channel
        }).then(() => {
            let msg = '';
            if (channel === 'email') {
                msg = platformIntl.get('msg.validate.send.email');
            } else if (channel === 'phone') {
                msg = platformIntl.get('msg.validate.send.phone');
            }
            if (msg) {
                Message.add({
                    messageType: 'toast',
                    type: 'success',
                    message: msg
                });
            }
        });
    };

    /**
     * 获取手机或邮箱的认证信息内容
     *
     * @param item 认证信息
     * @param channel 类型
     */
    const getVerifiedContent = (item: Record<string, any>, channel: 'email' | 'phone') => {
        const data = item[`${channel}_info`];
        if (data) {
            return (
                <span className="verified-content">
                    {data.is_verified ? (
                        <CheckCircleFilled className="verified-icon icon-select" />
                    ) : (
                        <>
                            <Tooltip
                                effect="light"
                                content={
                                    channel === 'email'
                                        ? platformIntl.get(`msg.target.email.unverified`)
                                        : platformIntl.get(`msg.target.phone.unverified`)
                                }
                                placement="top"
                            >
                                <ExclamationCircleFilled className="verified-icon icon-warning-circle" />
                            </Tooltip>
                            <Button
                                type="text"
                                size="small"
                                compact
                                onClick={() => sendValidation(item._id, channel)}
                            >
                                {platformIntl.get('msg.validate')}
                            </Button>
                        </>
                    )}
                </span>
            );
        }
        return null;
    };

    /**
     * 删除提醒对象
     */
    const removeReceiver = () => {
        const data = cloneDeep(receivers);
        remove(data, (item) => {
            return item._id === currentRowData._id;
        });
        onReceiverChange(data);
    };

    /**
     * 获取表格头部配置
     */
    const receiverTableColumns = useMemo(() => {
        // 需要预先在表格右侧留出一个滚动条宽度占位，大概是 12px，防止表格容器一直出现水平滚动条
        const { width: scrollBarWidth } = getScrollbarSize();
        const nameUnit = 5;
        const phoneUnit = 7;
        const emailUnit = 8;
        const operationUnit = 4;
        const totalUnit = nameUnit + phoneUnit + emailUnit + operationUnit;
        const flexWidth = tableWidth - scrollBarWidth;

        const columns = [
            {
                dataIndex: 'name',
                title: platformIntl.get('name'),
                width: (nameUnit / totalUnit) * flexWidth,
                render: (value: string) => {
                    return (
                        <div className="table-cell-content">
                            <span title={value}>{value}</span>
                        </div>
                    );
                }
            },
            {
                dataIndex: 'phone_info',
                title: platformIntl.get('phone'),
                width: (phoneUnit / totalUnit) * flexWidth,
                render: (item: IVerifiedData, rowData: Record<string, any>) => {
                    const text = get(item, 'phone', '');
                    return (
                        <div className="table-cell-content">
                            <span title={text}>{text}</span>
                            {getVerifiedContent(rowData, 'phone')}
                        </div>
                    );
                }
            },
            {
                dataIndex: 'email_info',
                title: platformIntl.get('email'),
                width: (emailUnit / totalUnit) * flexWidth,
                render: (item: IVerifiedData, rowData: Record<string, any>) => {
                    const text = get(item, 'email', '');
                    return (
                        <div className="table-cell-content">
                            <span title={text}>{text}</span>
                            {getVerifiedContent(rowData, 'email')}
                        </div>
                    );
                }
            }
        ];
        if (editable) {
            columns.push({
                dataIndex: 'action',
                title: platformIntl.get('order.operation'),
                width: (operationUnit / totalUnit) * flexWidth,
                render: (_value, rowData) => {
                    return (
                        <div className="operation-btn">
                            <SvgIcon
                                component={PencilEditIcon}
                                onClick={() => {
                                    onReceiverEdit(rowData);
                                }}
                            />
                            <SvgIcon
                                component={TrashIcon}
                                onClick={() => {
                                    setCurrentRowData(rowData);
                                    setAlertShow(true);
                                }}
                            />
                        </div>
                    );
                }
            });
        }
        if (!hasFunctionContactPhone()) {
            return filter(columns, (item) => item.dataIndex !== 'phone_info');
        }
        return columns;
    }, [editable, receivers, tableWidth]);

    return (
        <div className="fx-message-receiver-table">
            <VirtualTable
                fixedHeader
                size="large"
                hasOutBorder={false}
                columns={receiverTableColumns}
                dataSource={receivers}
                className="message-receiver-table"
                emptyContent={
                    <div className="receiver-empty-tip">{platformIntl.get('msg.target.empty')}</div>
                }
            />
            <Alert
                type="error"
                title={platformIntl.get('msg.target.delete.title')}
                message={platformIntl.get('msg.target.delete.content')}
                visible={alertShow}
                onCancel={() => setAlertShow(false)}
                onOk={() => {
                    setAlertShow(false);
                    post(getRequestUrl(API.message.delete_receiver, authModel.tenantId), {
                        _id: currentRowData._id
                    }).then(() => removeReceiver());
                }}
            />
        </div>
    );
};

export default MessageReceiverTable;
